// Adapted from OpenCharts (MIT, see LICENSE-OpenCharts).
import type { IChartApi, ISeriesApi, Logical, SeriesType, Time } from "lightweight-charts";
import type { Drawing, EntryState, ResolvedEntry, ResolvedFibLevel } from "./types";

export const FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1] as const;
export const FIB_EXT_LEVELS = [0, 0.618, 1, 1.618, 2.618] as const;

export type AnySeries = ISeriesApi<SeriesType>;

/**
 * Per-pass conversion context. Anchors are unix seconds that may fall between
 * this timeframe's bars (drawn on another timeframe) or past the loaded data
 * (future anchors): both need the series data and the bar interval.
 */
export interface ResolveCtx {
  chart: IChartApi;
  series: AnySeries;
  intervalSec: number;
  data: readonly { time: Time }[];
}

export function makeResolveCtx(chart: IChartApi, series: AnySeries, intervalSec: number): ResolveCtx {
  return { chart, series, intervalSec, data: series.data() };
}

// ── Time ↔ pixel, with interpolation between bars and extrapolation past them ──

/** First index whose bar time is >= time (data is sorted ascending). */
function lowerBound(data: readonly { time: Time }[], time: number): number {
  let lo = 0;
  let hi = data.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((data[mid]!.time as number) < time) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Time → x, interpolating off-grid anchors and extrapolating past either end. */
export function timeToX(ctx: ResolveCtx, time: number): number | null {
  const ts = ctx.chart.timeScale();
  const direct = ts.timeToCoordinate(time as Time);
  if (direct !== null) return direct;
  const logical = timeToLogical(ctx, time);
  if (logical === null) return null;
  return logicalToX(ts, logical);
}

/**
 * `logicalToCoordinate` returns 0 for a non-integer logical index, which would
 * snap every off-grid anchor to the left edge. Integer logicals are always
 * valid (and extrapolate past the data), so interpolate between the two.
 */
function logicalToX(ts: ReturnType<IChartApi["timeScale"]>, logical: number): number | null {
  const lo = Math.floor(logical);
  const xLo = ts.logicalToCoordinate(lo as Logical);
  if (xLo === null) return null;
  const frac = logical - lo;
  if (frac === 0) return xLo;
  const xHi = ts.logicalToCoordinate((lo + 1) as Logical);
  if (xHi === null) return null;
  return xLo + frac * (xHi - xLo);
}

export function timeToLogical(ctx: Pick<ResolveCtx, "data" | "intervalSec">, time: number): number | null {
  const { data, intervalSec } = ctx;
  if (data.length === 0 || intervalSec <= 0) return null;
  const firstTime = data[0]!.time as number;
  const lastTime = data[data.length - 1]!.time as number;
  if (time <= firstTime) return (time - firstTime) / intervalSec;
  if (time >= lastTime) return data.length - 1 + (time - lastTime) / intervalSec;
  const idx = lowerBound(data, time);
  const t1 = data[idx - 1]!.time as number;
  const t2 = data[idx]!.time as number;
  return idx - 1 + (time - t1) / Math.max(t2 - t1, 1);
}

/** X → time, stepping whole bars into the empty space past the data. */
export function xToTime(ctx: ResolveCtx, x: number): number | null {
  const direct = ctx.chart.timeScale().coordinateToTime(x);
  if (direct !== null) return direct as number;
  const { data, intervalSec } = ctx;
  if (data.length === 0 || intervalSec <= 0) return null;
  const logical = ctx.chart.timeScale().coordinateToLogical(x);
  if (logical === null) return null;
  const lastIdx = data.length - 1;
  const lastTime = data[lastIdx]!.time as number;
  const firstTime = data[0]!.time as number;
  if (logical > lastIdx) return lastTime + Math.round(logical - lastIdx) * intervalSec;
  if (logical < 0) return firstTime + Math.round(logical) * intervalSec;
  return null;
}

// ── Drawings ────────────────────────────────────────────────────────────────

/** A drawing's anchors in pane pixels. */
export function resolveEntry(d: Drawing, ctx: ResolveCtx, state: EntryState): ResolvedEntry {
  const entry: ResolvedEntry = {
    d,
    x1: d.time != null ? timeToX(ctx, d.time) : null,
    y1: Number.isFinite(d.price) ? ctx.series.priceToCoordinate(d.price) : null,
    x2: d.time2 != null ? timeToX(ctx, d.time2) : null,
    y2: d.price2 != null ? ctx.series.priceToCoordinate(d.price2) : null,
    state,
  };
  if (d.type === "fibonacci" || d.type === "fibextension") {
    entry.fibLevels = resolveFibLevels(d, ctx.series);
  }
  if (d.type === "trendline") {
    entry.seg = trendlineSegment(entry, ctx.chart.timeScale().width());
    if (state === "selected" || state === "preview") entry.stats = trendlineStats(d, ctx);
  }
  if (d.type === "position") {
    entry.yStop = d.stopPrice != null ? ctx.series.priceToCoordinate(d.stopPrice) : null;
    entry.yTarget = d.targetPrice != null ? ctx.series.priceToCoordinate(d.targetPrice) : null;
  }
  if (d.type === "channel") {
    entry.x3 = d.time3 != null ? timeToX(ctx, d.time3) : null;
    entry.y3 = d.price3 != null ? ctx.series.priceToCoordinate(d.price3) : null;
  }
  return entry;
}

/** Change, % and bar count between a trendline's two ends. */
export function trendlineStats(d: Drawing, ctx: Pick<ResolveCtx, "intervalSec" | "series">): string[] {
  if (d.price2 == null || d.time == null || d.time2 == null) return [];
  const dp = d.price2 - d.price;
  const pct = d.price !== 0 ? (dp / d.price) * 100 : 0;
  const bars = ctx.intervalSec > 0 ? Math.round(Math.abs(d.time2 - d.time) / ctx.intervalSec) : 0;
  const formatted = ctx.series.priceFormatter().format(Math.abs(dp));
  return [`${dp >= 0 ? "+" : "−"}${formatted} (${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%)`, `${bars} bars`];
}

type Segment = NonNullable<ResolvedEntry["seg"]>;

// The anchors left→right, stretched to the pane edges when extended.
function trendlineSegment(e: ResolvedEntry, paneWidth: number): Segment | undefined {
  if (e.x1 === null || e.y1 === null || e.x2 === null || e.y2 === null) return undefined;
  const seg: Segment =
    e.x1 <= e.x2 ? { x1: e.x1, y1: e.y1, x2: e.x2, y2: e.y2 } : { x1: e.x2, y1: e.y2, x2: e.x1, y2: e.y1 };
  if ((!e.d.extendLeft && !e.d.extendRight) || seg.x2 - seg.x1 < 0.5) return seg;
  const slope = (seg.y2 - seg.y1) / (seg.x2 - seg.x1);
  if (e.d.extendLeft) {
    seg.y1 -= slope * seg.x1;
    seg.x1 = 0;
  }
  if (e.d.extendRight && paneWidth > seg.x2) {
    seg.y2 += slope * (paneWidth - seg.x2);
    seg.x2 = paneWidth;
  }
  return seg;
}

/**
 * Fib prices, TradingView's direction: level 0 at the second anchor, level 1
 * at the first — draw from the swing's start to its end and 0.618 sits where
 * a pullback of 61.8% would reach.
 */
export function fibPrices(d: Drawing): { level: number; price: number }[] {
  if (d.price2 == null) return [];
  const levels = d.type === "fibextension" ? FIB_EXT_LEVELS : FIB_LEVELS;
  return levels.map(level => ({ level, price: d.price2! + (d.price - d.price2!) * level }));
}

function resolveFibLevels(d: Drawing, series: AnySeries): ResolvedFibLevel[] {
  const formatter = series.priceFormatter();
  return fibPrices(d).map(({ level, price }) => ({
    level,
    price,
    y: series.priceToCoordinate(price),
    label: `${level} (${formatter.format(price)})`,
  }));
}
